import { Component, Input, OnInit } from '@angular/core';
import { ReactiveFormsModule, Validators } from '@angular/forms';
import {
  MatIconModule,
  MatCheckboxModule,
  MatFormFieldModule,
  MatSlideToggleModule,
  MatTooltipModule,
  MatLabel,
  MatSelectModule,
  MatInputModule,
  MatButton,
} from 'src/app/shared/material';
import { PillButtonGroupComponent } from '../pill-button-group-control/pill-button-group.component';
import { NeemVersionPillComponent } from '../neem-version-pill-control/neem-version-pill.component';
import { ControlType } from '../../models/control-type.enum';
import { DynamicFormControl } from '../../models/dynamic-form-control.model';
import { MultiSelectControlComponent } from '../multi-select-control/multi-select-control.component';
import { TextBoxControlComponent } from '../text-box-control/text-box-control.component';
import { DateRangeControlComponent } from '../date-range-control/date-range-control.component';
import { DatePickerControlComponent } from '../date-picker-control/date-picker-control.component';
import { CommonModule } from '@angular/common';
import { CustomerInputControlComponent } from '../customer-input-control/customer-input-control.component';
import { UsStateService } from '../../services/form-us-state.service';
import { USState } from '../../models/us-states.model';
import { SbraDropdownControlComponent } from '../sbra-dropdown-control/sbra-dropdown-control.component';
import { delay, distinctUntilChanged, takeWhile, Subscription } from 'rxjs';
import { SubmissionConstants } from 'src/app/modules/energy-credits/models/constants/submissions-constants';
import { ClimateZoneControlComponent } from '../climate-zone-control/climate-zone.component';
import { TextAreaComponent } from '../text-area-control/text-area-control.component';
import { HeatPumpInvoiceDownloadService } from '../../../../modules/energy-credits/services/heat-pump-invoice-download.service';
import { SelectControlComponent } from '../select-control/select-control.component';
import { BannerComponent } from '../banner/banner.component';
import { DYNAMIC_ERROR_STATE_MATCHER } from '../../services/dynamic-control-error-state-matcher';

@Component({
    selector: 'lib-dynamic-form-control',
    standalone: true,
    imports: [
        CommonModule,
        MatLabel,
        ReactiveFormsModule,
        MatFormFieldModule,
        MatSelectModule,
        MatIconModule,
        MatCheckboxModule,
        MatInputModule,
        MatButton,
        PillButtonGroupComponent,
        MatSlideToggleModule,
        MatTooltipModule,
        MultiSelectControlComponent,
        TextBoxControlComponent,
        DateRangeControlComponent,
        DatePickerControlComponent,
        CustomerInputControlComponent,
        SbraDropdownControlComponent,
        ClimateZoneControlComponent,
        TextAreaComponent,
        SelectControlComponent,
        BannerComponent,
    NeemVersionPillComponent,
    ],
    templateUrl: './dynamic-form-control.component.html',
    styleUrl: './dynamic-form-control.component.scss'
})
export class DynamicFormControlComponent implements OnInit {
  @Input() control: DynamicFormControl;
  /** CUST_INPUT - control specific to the customer information for titleCase formatting */
  @Input() custControl: DynamicFormControl;
  private _subscriptions: Subscription[] = [];
  private formStructureId: any;

  stateValues: USState[] = [];
  errorStateMatcher = DYNAMIC_ERROR_STATE_MATCHER;

  constructor(
    private heatPumpInvoiceDownloadService: HeatPumpInvoiceDownloadService,
    public stateService: UsStateService,
    private submissionConstant: SubmissionConstants,
  ) {
    this.stateService.getAllStates().subscribe((data) => {
      const sortedStates = [...data];
      sortedStates.sort((a, b) => a.Name.localeCompare(b.Name));
      this.stateValues = sortedStates;
    });
    this.formStructureId = localStorage.getItem(
      this.submissionConstant.formStructureIdKey,
    );
  }

  ngOnInit(): void {
    if (
      (this.formStructureId === '2' || this.formStructureId === '4') &&
      this.control.formControls[0].formControl.parent &&
      this.control.formControls[0].propertyBinding === 'designPackage'
    ) {
      this.formV2ControlSetup();
    }
    // SBRA V3 Heat Pump Document on File behavior
    if (
      this.formStructureId === '1' &&
      this.control.formControls[0].propertyBinding ===
        'primarySpaceHeatingCooling'
    ) {
      const primarySpaceHeatingCoolingControl =
        this.control.formControls[0].formControl.parent.get(
          'primarySpaceHeatingCooling',
        );
      const form = this.control.formControls[0].formControl.parent;
      const heatPumpControl =
        this.control.formControls[0].formControl.parent.get(
          'isHeatPumpDocumentOnFile',
        );
      const statusControl =
        this.control.formControls[0].formControl.parent.get('status');
      const statusSub = statusControl.valueChanges.subscribe((status) => {
        if (
          status === 'Ready To Submit' &&
          heatPumpControl.getRawValue() === null
        ) {
          heatPumpControl.setErrors({ required: true });
        }
      });
      const primarySpaceHeatingCoolingSub =
        primarySpaceHeatingCoolingControl.valueChanges
          .pipe(distinctUntilChanged(), delay(20))
          .subscribe((ctrlVal) => {
            if (primarySpaceHeatingCoolingControl.status !== 'DISABLED') {
              if (
                ctrlVal === 'Elec Heat Pump w/ Elec Furnace' &&
                heatPumpControl.getRawValue() !== null
              ) {
                form.patchValue({ isHeatPumpDocumentOnFile: null } as any);
              } else if (ctrlVal !== 'Elec Heat Pump w/ Elec Furnace') {
                if (form.value.isHeatPumpDocumentOnFile !== false) {
                  form.patchValue({ isHeatPumpDocumentOnFile: false } as any, {
                    emitEvent: true,
                  });
                }
                heatPumpControl.setErrors(null);
              }
            }
          });
      this._subscriptions.push(statusSub, primarySpaceHeatingCoolingSub);
    }
    if (
      this.formStructureId === '3' &&
      this.control.formControls[0].propertyBinding ===
        'primarySpaceHeatingCooling'
    ) {
      this.formNeemV3ControlSetup();
    }
  }

  formV2ControlSetup() {
    const form = this.control.formControls[0].formControl.parent;
    const designPackageControl =
      this.control.formControls[0].formControl.parent.get('designPackage');
    const heatPumpControl = this.control.formControls[0].formControl.parent.get(
      'isHeatPumpDocumentOnFile',
    );
    const statusControl =
      this.control.formControls[0].formControl.parent.get('status');

    const statusSub = statusControl.valueChanges
      .pipe(takeWhile(() => statusControl.value !== 'Submitted'))
      .subscribe((status) => {
        if (this.control.formControls[0].formControl.value === null) {
          if (
            status !== 'Ready To Submit' &&
            status !== 'Submitted Manually'
          ) {
            this.control.formControls[0].formControl.setErrors(null);
          } else if (
            status === 'Ready To Submit' ||
            status === 'Submitted Manually'
          ) {
            this.control.formControls[0].formControl.setErrors({
              invalid: true,
            });
            heatPumpControl.setErrors({ required: true });
          }
        } else if (heatPumpControl.value === null) {
          heatPumpControl.setErrors({ required: true });
        }
      });

    const designPackageSub = designPackageControl.valueChanges
      .pipe(takeWhile(() => statusControl.value !== 'Submitted'))
      .subscribe((design) => {
        if (design === '1-EHP' || design === '2-EHP' || design === '3-EHP') {
          setTimeout(() => {
            form.patchValue({ isHeatPumpDocumentOnFile: null } as any);
            const error =
              statusControl.value === 'Ready To Submit' ||
              statusControl.value === 'Submitted Manually'
                ? { required: true }
                : null;
            heatPumpControl.setErrors(error);
          }, 50);
        } else {
          heatPumpControl.setErrors(null);
        }
      });

    this._subscriptions.push(designPackageSub, statusSub);
  }
  formNeemV3ControlSetup(): void {
    // logic for primarySpaceHeatingCooling
    const form = this.control.formControls[0].formControl.parent;
    const primarySpaceHeatingCoolingControl =
      this.control.formControls[0].formControl.parent.get(
        'primarySpaceHeatingCooling',
      );
    const primarySpaceHeatingCooling =
      primarySpaceHeatingCoolingControl.valueChanges.subscribe(
        (heatingCooling) => {
          if (heatingCooling === '') {
            setTimeout(() => {
              form.patchValue({ hasHeatPump: true } as any);
            }, 50);
          } else {
            form.patchValue({
              hasHeatPump: false,
              isHeatPumpDocumentOnFile: null,
            } as any);
          }
        },
      );

    this._subscriptions.push(primarySpaceHeatingCooling);
  }

  handleControl(propertyBinding: string): void {
    if (propertyBinding === 'downloadHeatPumpInvoice') {
      const serialNumber =
        this.control.formControls[0].formControl.parent.get(
          'homeSerialNumber',
        ).value;
      this.heatPumpInvoiceDownloadService.downloadHeatPumpInvoiceDocuments(
        serialNumber,
      );
    }
  }

  ngOnDestroy() {
    this._subscriptions.forEach((i) => i.unsubscribe());
  }

  public get ControlType(): typeof ControlType {
    return ControlType;
  }

  /**
   * Drives the Material required-asterisk explicitly for controls rendered
   * inline in this template (e.g. STATE_SELECT). Material's implicit validator
   * detection fails to render the asterisk for controls that stay empty (such
   * as a disabled, pre-populated field) because no `stateChanges` event is
   * emitted to refresh the form field.
   */
  public get isRequired(): boolean {
    return (
      this.control?.customOptions?.isRequired ||
      this.control?.formControls?.[0]?.formControl?.hasValidator(
        Validators.required,
      ) ||
      false
    );
  }
}
